import { fromUnixTime, getTime } from "date-fns";
import { ReactNode, useEffect } from "react";
import { useImmer } from "use-immer";

import { IgButton } from "@intelligems/ig-theme/IgButton";
import { IgCheckbox } from "@intelligems/ig-theme/IgCheckbox";
import { IgDivider } from "@intelligems/ig-theme/IgDivider";
import { IgSelect } from "@intelligems/ig-theme/IgSelect";
import { IgTextField } from "@intelligems/ig-theme/IgTextField";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { StyledSwitch } from "@intelligems/ig-theme/IgSwitch";
import type {
  AnalyticsChannelType,
  ConversionStageReachedType,
  DeviceType,
  LandingPageFilterType,
  VisitorType,
} from "@intelligems/ig-types/schemas/analytics/base";
import { CountryEnum } from "@intelligems/ig-types/schemas/shopify";
import {
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  SelectChangeEvent,
  Stack,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { MobileDateTimePicker } from "@mui/x-date-pickers/MobileDateTimePicker";
import { FilterTypeMenuItems } from "@src/components/targeting/components/FilterTypeMenuItems";
import { getConversionStageReachedList } from "@src/components/AudienceFilters/utils";
import { TRAFFIC_CONFIG_QUERY_PARAM_OPTIONS } from "@src/components/targeting/utils/constants";
import { useGetAnalyticsCountries } from "@src/features/analytics/hooks/use-get-analytics-countries";
import { useDispatch, useSelector } from "@src/store";
import { getAnalyticsProducts } from "@src/store/slices/analyticsProducts";
import { getHasAnyCheckoutBlocks } from "@src/store/slices/checkoutBlocks/selectors";
import {
  getExperienceAnalyticsFilters,
  setDashboardFilter,
} from "@src/store/slices/dashboardFilters";
import { selectedExperience } from "@src/store/slices/experiences";
import { getOfferProducts } from "@src/store/slices/offerProducts";
import { IExperienceDashboardFilters } from "@src/store/types/dashboardFilters";
import { mpMobileFilterSaved } from "@src/utils/mixpanel";

import { MobileFilterAutocomplete } from "../shared/components/MobileFilterAutocomplete";
import { mobileInputSx } from "../shared/constants/mobileInputStyles";
import { useMobileDrawer } from "../shared/hooks/useMobileDrawer";

export const FilterDrawerContent = () => {
  const dispatch = useDispatch();
  const drawer = useMobileDrawer();
  const dashboardFilters = useSelector(getExperienceAnalyticsFilters);
  const allCountryCodesData = useGetAnalyticsCountries();
  const experience = useSelector(selectedExperience);
  const hasCheckoutBlocks = useSelector(getHasAnyCheckoutBlocks());
  const hasOnsiteUpsell = experience?.testTypes.hasTestOnsiteUpsell ?? false;
  const analyticsProducts = useSelector(getAnalyticsProducts);
  const offerProducts = useSelector(getOfferProducts);

  const hideTargetProductOption =
    !experience?.testTypes.hasTestPricing &&
    analyticsProducts?.length === 0 &&
    offerProducts?.length === 0;

  const initializeLocalFilters = (f: IExperienceDashboardFilters) => ({
    deviceType: f.deviceType,
    visitorType: f.visitorType,
    channels: f.channels,
    startTime: f.startTime
      ? fromUnixTime(
          typeof f.startTime === "string"
            ? parseInt(f.startTime)
            : f.startTime / 1000
        )
      : null,
    endTime: f.endTime
      ? fromUnixTime(
          typeof f.endTime === "string" ? parseInt(f.endTime) : f.endTime / 1000
        )
      : null,
    urlParam: f.urlParam,
    landingPage: f.landingPage,
    userBehavior: f.userBehavior,
    reachedCheckoutStage: f.reachedCheckoutStage || "any",
    countryCodes: f.countryCodes || [],
    analyticsViewType: f.analyticsViewType,
    minNetRevenue: f.minNetRevenue,
    maxNetRevenue: f.maxNetRevenue,
    isZScoreEnabled: f.isZScoreEnabled ?? true,
    maxZScore: f.maxZScore,
  });

  const [localFilters, setLocalFilters] = useImmer(
    initializeLocalFilters(dashboardFilters)
  );

  useEffect(() => {
    setLocalFilters(initializeLocalFilters(dashboardFilters));
  }, [dashboardFilters, setLocalFilters]);

  const handleDeviceTypeChange = (value: DeviceType) => {
    setLocalFilters((draft) => {
      draft.deviceType = value;
    });
  };

  const handleVisitorTypeChange = (value: VisitorType) => {
    setLocalFilters((draft) => {
      draft.visitorType = value;
    });
  };

  const handleChannelChange = (
    channel: AnalyticsChannelType,
    checked: boolean
  ) => {
    setLocalFilters((draft) => {
      if (checked) {
        if (!draft.channels.includes(channel)) {
          draft.channels.push(channel);
        }
      } else {
        draft.channels = draft.channels.filter((c) => c !== channel);
      }
    });
  };

  const handleAllSourcesChange = (checked: boolean) => {
    setLocalFilters((draft) => {
      if (checked) {
        draft.channels = [];
      } else {
        draft.channels = [...allChannels];
      }
    });
  };

  const handleStartTimeChange = (value: Date | null) => {
    setLocalFilters((draft) => {
      draft.startTime = value;
    });
  };

  const handleEndTimeChange = (value: Date | null) => {
    setLocalFilters((draft) => {
      draft.endTime = value;
    });
  };

  const handleUrlParamChange = (
    field: "queryParam" | "queryParamFilterType" | "queryParamValue",
    value: string | null
  ) => {
    setLocalFilters((draft) => {
      if (!draft.urlParam) {
        draft.urlParam = {
          queryParam: null,
          queryParamValue: null,
          queryParamFilterType: null,
        };
      }
      draft.urlParam[field] = value as LandingPageFilterType;
    });
  };

  const handleLandingPageChange = (
    field: "landingPageUrlPath" | "landingPageUrlPathFilterType",
    value: LandingPageFilterType | null
  ) => {
    setLocalFilters((draft) => {
      if (!draft.landingPage) {
        draft.landingPage = {
          landingPageUrlPath: null,
          landingPageUrlPathFilterType: null,
        };
      }
      if (field === "landingPageUrlPath") {
        draft.landingPage[field] = value as string | null;
      } else {
        draft.landingPage[field] = value;
      }
    });
  };

  const handleConversionStageChange = (value: ConversionStageReachedType) => {
    setLocalFilters((draft) => {
      draft.reachedCheckoutStage = value;
    });
  };

  const handleUserBehaviorChange = (
    field:
      | "viewedCheckout"
      | "addedToCart"
      | "viewedCollectionPage"
      | "viewedProductPage",
    value: boolean
  ) => {
    setLocalFilters((draft) => {
      if (!draft.userBehavior) {
        draft.userBehavior = {
          viewedCheckout: null,
          addedToCart: null,
          viewedCollectionPage: null,
          viewedProductPage: null,
        };
      }
      draft.userBehavior[field] = value;
    });
  };

  const handleCountryCodesChange = (values: string[]) => {
    setLocalFilters((draft) => {
      draft.countryCodes = values;
    });
  };

  const handleAnalyticsViewTypeChange = (
    value: "sitewide_orders" | "test_orders_only"
  ) => {
    setLocalFilters((draft) => {
      draft.analyticsViewType = value;
    });
  };

  const handleRevenueChange = (
    field: "minNetRevenue" | "maxNetRevenue",
    value: string
  ) => {
    setLocalFilters((draft) => {
      const numValue = value === "" ? null : parseFloat(value);
      draft[field] = numValue;
    });
  };

  const handleZScoreToggle = (checked: boolean) => {
    setLocalFilters((draft) => {
      draft.isZScoreEnabled = checked;
    });
  };

  const handleZScoreChange = (value: string) => {
    setLocalFilters((draft) => {
      draft.maxZScore = value === "" ? null : parseFloat(value);
    });
  };

  const handleSaveFilters = () => {
    // Apply all local filter changes to Redux at once
    dispatch(
      setDashboardFilter({
        ...dashboardFilters,
        deviceType: localFilters.deviceType,
        visitorType: localFilters.visitorType,
        channels: localFilters.channels,
        startTime: localFilters.startTime
          ? getTime(localFilters.startTime)
          : null,
        endTime: localFilters.endTime ? getTime(localFilters.endTime) : null,
        urlParam: localFilters.urlParam,
        landingPage: localFilters.landingPage,
        userBehavior: localFilters.userBehavior,
        reachedCheckoutStage: localFilters.reachedCheckoutStage,
        countryCodes: localFilters.countryCodes,
        analyticsViewType: localFilters.analyticsViewType,
        minNetRevenue: localFilters.minNetRevenue,
        maxNetRevenue: localFilters.maxNetRevenue,
        isZScoreEnabled: localFilters.isZScoreEnabled,
        maxZScore: localFilters.maxZScore,
      })
    );

    mpMobileFilterSaved(experience);
    drawer.closeDrawer();
  };

  const isAllSourcesChecked = localFilters.channels.length === 0;

  const allChannels: AnalyticsChannelType[] = [
    "Direct",
    "Email",
    "Referral",
    "Organic Social",
    "Paid Social",
    "Organic Search",
    "Paid Search",
    "Paid Shopping",
    "SMS",
    "Google",
    "Facebook",
    "Instagram",
    "TikTok",
    "Twitter",
    "Youtube",
  ];

  const maxStartTime = new Date(
    (localFilters.endTime?.getTime() ?? Date.now()) - 1000 * 60 * 60
  );

  return (
    <Stack sx={{ p: 2 }} spacing={4}>
      <FilterSection title="Date Range">
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Stack direction="row" spacing={2} sx={{ paddingTop: 4 }}>
            <MobileDateTimePicker
              label="Start time"
              value={localFilters.startTime}
              onChange={handleStartTimeChange}
              format="MM/dd/yyyy hh:mm a"
              maxDateTime={maxStartTime}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  sx: mobileInputSx,
                },
                layout: {
                  sx: {
                    ".MuiDateTimePickerToolbar-timeDigitsContainer": {
                      alignItems: "center",
                    },
                  },
                },
              }}
            />
            <MobileDateTimePicker
              label="End time"
              value={localFilters.endTime}
              onChange={handleEndTimeChange}
              format="MM/dd/yyyy hh:mm a"
              minDateTime={localFilters.startTime ?? undefined}
              maxDateTime={new Date()}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  sx: mobileInputSx,
                },
                layout: {
                  sx: {
                    ".MuiDateTimePickerToolbar-timeDigitsContainer": {
                      alignItems: "center",
                    },
                  },
                },
              }}
            />
          </Stack>
        </LocalizationProvider>
      </FilterSection>
      <IgDivider />

      <FilterSection title="Device Type">
        <RadioGroup
          value={localFilters.deviceType}
          onChange={(e) => handleDeviceTypeChange(e.target.value as DeviceType)}
        >
          <FormControlLabel
            value="any"
            control={<Radio />}
            label="All devices"
          />
          <FormControlLabel
            value="desktop"
            control={<Radio />}
            label="Desktop"
          />
          <FormControlLabel value="mobile" control={<Radio />} label="Mobile" />
        </RadioGroup>
      </FilterSection>
      <IgDivider />

      <FilterSection title="Visitor type">
        <RadioGroup
          value={localFilters.visitorType}
          onChange={(e) =>
            handleVisitorTypeChange(e.target.value as VisitorType)
          }
        >
          <FormControlLabel
            value="any"
            control={<Radio />}
            label="All visitors"
          />
          <FormControlLabel value="new" control={<Radio />} label="New" />
          <FormControlLabel
            value="returning"
            control={<Radio />}
            label="Returning"
          />
        </RadioGroup>
      </FilterSection>
      <IgDivider />

      <FilterSection title="Source channels & sites">
        <FormControlLabel
          control={
            <IgCheckbox
              checked={isAllSourcesChecked}
              onChange={(e) => handleAllSourcesChange(e.target.checked)}
            />
          }
          label="All sources"
        />
        {allChannels.map((channel) => (
          <FormControlLabel
            key={channel}
            control={
              <IgCheckbox
                checked={
                  isAllSourcesChecked || localFilters.channels.includes(channel)
                }
                disabled={isAllSourcesChecked}
                onChange={(e) => handleChannelChange(channel, e.target.checked)}
              />
            }
            label={channel}
          />
        ))}
      </FilterSection>

      <IgDivider />

      <FilterSection title="URL Parameter">
        <Stack spacing={2}>
          <IgSelect
            label="Parameter"
            value={localFilters.urlParam?.queryParam || ""}
            onChange={(e: SelectChangeEvent) =>
              handleUrlParamChange("queryParam", e.target.value || null)
            }
            fullWidth
            size="small"
            sx={mobileInputSx}
          >
            <MenuItem value="">None</MenuItem>
            {Object.entries(TRAFFIC_CONFIG_QUERY_PARAM_OPTIONS).map(
              ([key, value]) => (
                <MenuItem key={key} value={value}>
                  {value}
                </MenuItem>
              )
            )}
          </IgSelect>
          <IgSelect
            label="Filter Type"
            value={localFilters.urlParam?.queryParamFilterType || ""}
            onChange={(e: SelectChangeEvent) =>
              handleUrlParamChange(
                "queryParamFilterType",
                e.target.value || null
              )
            }
            fullWidth
            size="small"
            sx={mobileInputSx}
          >
            <MenuItem value="">None</MenuItem>
            {FilterTypeMenuItems()}
          </IgSelect>
          <IgTextField
            label="Value"
            value={localFilters.urlParam?.queryParamValue || ""}
            onChange={(e) =>
              handleUrlParamChange("queryParamValue", e.target.value || null)
            }
            disabled={
              localFilters.urlParam?.queryParamFilterType === "isNull" ||
              localFilters.urlParam?.queryParamFilterType === "isNotNull"
            }
            fullWidth
            size="small"
            sx={mobileInputSx}
          />
        </Stack>
      </FilterSection>

      <IgDivider />

      <FilterSection title="Conversion Funnel">
        <Stack spacing={2}>
          <RadioGroup
            value={localFilters.reachedCheckoutStage}
            onChange={(e) =>
              handleConversionStageChange(
                e.target.value as ConversionStageReachedType
              )
            }
          >
            {getConversionStageReachedList({
              hasCheckoutBlocks,
              hasOnsiteUpsell,
              hasTestPostPurchase:
                experience?.testTypes.hasTestPostPurchase ?? false,
            }).map((stage) => (
              <FormControlLabel
                key={stage.key}
                value={stage.key}
                control={<Radio />}
                label={stage.label}
              />
            ))}
          </RadioGroup>
        </Stack>
      </FilterSection>

      <IgDivider />

      <FilterSection title="Views">
        <FormControlLabel
          control={
            <IgCheckbox
              checked={localFilters.userBehavior?.viewedCollectionPage ?? false}
              onChange={(e) =>
                handleUserBehaviorChange(
                  "viewedCollectionPage",
                  e.target.checked
                )
              }
            />
          }
          label="Visited Collection"
        />
        <FormControlLabel
          control={
            <IgCheckbox
              checked={localFilters.userBehavior?.viewedProductPage ?? false}
              onChange={(e) =>
                handleUserBehaviorChange("viewedProductPage", e.target.checked)
              }
            />
          }
          label="Visited Product Page"
        />
      </FilterSection>

      <IgDivider />

      <FilterSection title="Filter Orders by Product">
        <RadioGroup
          value={localFilters.analyticsViewType}
          onChange={(e) =>
            handleAnalyticsViewTypeChange(
              e.target.value as "sitewide_orders" | "test_orders_only"
            )
          }
        >
          <FormControlLabel
            value="sitewide_orders"
            control={<Radio />}
            label="All Orders"
          />
          {!hideTargetProductOption && (
            <FormControlLabel
              value="test_orders_only"
              control={<Radio />}
              label="Orders with Target Products"
            />
          )}
        </RadioGroup>
      </FilterSection>

      <IgDivider />

      <FilterSection title="Countries">
        <MobileFilterAutocomplete
          selectedValues={localFilters.countryCodes}
          onChange={handleCountryCodesChange}
          filterKey="countryCodes"
          allDataOptions={allCountryCodesData || []}
          placeholder="Select countries"
          disableCloseOnSelect={true}
          getOptionLabel={(option: string) =>
            `${CountryEnum[option as keyof typeof CountryEnum] ?? "Unknown"} ${option ? `(${option})` : ""}`
          }
        />
      </FilterSection>

      <IgDivider />

      <FilterSection title="Landing Page">
        <Stack spacing={2}>
          <IgSelect
            label="Filter Type"
            value={localFilters.landingPage?.landingPageUrlPathFilterType || ""}
            onChange={(e: SelectChangeEvent) =>
              handleLandingPageChange(
                "landingPageUrlPathFilterType",
                (e.target.value as LandingPageFilterType) || null
              )
            }
            fullWidth
            size="small"
            sx={mobileInputSx}
          >
            <MenuItem value="">None</MenuItem>
            {FilterTypeMenuItems()}
          </IgSelect>
          <IgTextField
            label="URL Path"
            value={localFilters.landingPage?.landingPageUrlPath || ""}
            onChange={(e) =>
              handleLandingPageChange(
                "landingPageUrlPath",
                (e.target.value as LandingPageFilterType) ?? null
              )
            }
            disabled={
              localFilters.landingPage?.landingPageUrlPathFilterType ===
                "isNull" ||
              localFilters.landingPage?.landingPageUrlPathFilterType ===
                "isNotNull"
            }
            fullWidth
            size="small"
            sx={mobileInputSx}
          />
        </Stack>
      </FilterSection>

      <IgDivider />

      <FilterSection title="Order Value">
        <Stack spacing={2}>
          <IgTextField
            label="Min order value"
            type="number"
            value={localFilters.minNetRevenue ?? ""}
            onChange={(e) =>
              handleRevenueChange("minNetRevenue", e.target.value)
            }
            fullWidth
            size="small"
            slotProps={{
              htmlInput: {
                min: 0,
                step: 0.01,
              },
            }}
            sx={mobileInputSx}
          />
          <IgTextField
            label="Max order value"
            type="number"
            value={localFilters.maxNetRevenue ?? ""}
            onChange={(e) =>
              handleRevenueChange("maxNetRevenue", e.target.value)
            }
            fullWidth
            size="small"
            slotProps={{
              htmlInput: {
                min: 0,
                step: 0.01,
              },
            }}
            sx={mobileInputSx}
          />
        </Stack>
      </FilterSection>

      <IgDivider />

      <FilterSection title="Z-Score">
        <Stack spacing={2}>
          <FormControlLabel
            control={
              <StyledSwitch
                onChange={(e) => handleZScoreToggle(e.target.checked)}
                checked={localFilters.isZScoreEnabled}
              />
            }
            label={<IgTypography>Enable Max Z-Score</IgTypography>}
            labelPlacement="start"
            sx={{ justifyContent: "space-between", margin: 0 }}
          />
          <IgTextField
            label="Max Z-Score"
            type="number"
            value={
              localFilters.isZScoreEnabled
                ? (localFilters.maxZScore ?? "")
                : "9999"
            }
            onChange={(e) => handleZScoreChange(e.target.value)}
            disabled={!localFilters.isZScoreEnabled}
            fullWidth
            size="small"
            slotProps={{
              htmlInput: {
                min: 0,
                step: 0.1,
              },
            }}
            sx={mobileInputSx}
          />
        </Stack>
      </FilterSection>

      <IgDivider />

      <IgButton
        variant="contained"
        fullWidth
        onClick={handleSaveFilters}
        color="primary"
        sx={{ position: "sticky", bottom: 0 }}
      >
        Save filters
      </IgButton>
    </Stack>
  );
};

type FilterSectionProps = {
  title: string;
  children: ReactNode;
};

const FilterSection = ({ title, children }: FilterSectionProps) => {
  return (
    <Stack>
      <IgTypography
        variant="subtitle2"
        sx={{
          fontWeight: 600,
          paddingBottom: 1,
        }}
      >
        {title}
      </IgTypography>
      {children}
    </Stack>
  );
};
